import * as React from 'react';
import { cn } from '../../utils';
import { useTranslation } from '../../providers/UIProvider';
import { Button } from '../Button';
import { Icon } from '../icons/Icon';

export interface FilterEditorPanelProps {
  /** Parameter name shown in the header */
  title: string;
  /** Shows the back chevron; returns to the parameter list */
  onBack?: () => void;
  /** Accessible name of the back button */
  backLabel: string;
  /** The value editor */
  children: React.ReactNode;
  /** Shows the ghost clear button on the left of the footer */
  onClear?: () => void;
  /** Defaults to `components.filterEditorPanel.clear` */
  clearLabel?: string;
  onApply: () => void;
  applyLabel: string;
  applyDisabled?: boolean;
  /** Caption under the editor */
  hint?: string;
  className?: string;
  testId: string;
}

/** Popover content of one filter: header with back and title, editor, clear and apply footer. */
export const FilterEditorPanel = React.forwardRef<HTMLDivElement, FilterEditorPanelProps>(
  (
    {
      title,
      onBack,
      backLabel,
      children,
      onClear,
      clearLabel,
      onApply,
      applyLabel,
      applyDisabled,
      hint,
      className,
      testId
    },
    ref
  ) => {
    const t = useTranslation('components.filterEditorPanel');
    return (
      <div
        ref={ref}
        data-testid={testId}
        className={cn('flex flex-col gap-xs p-sm min-w-65 text-sm', className)}
      >
        <div className='flex items-center gap-sm px-xs pt-xs pb-sm border-b border-border-base'>
          {onBack && (
            <button
              type='button'
              aria-label={backLabel}
              data-testid={`${testId}-back`}
              onClick={onBack}
              className={cn(
                'inline-flex items-center justify-center size-6 rounded-md cursor-pointer',
                'text-foreground-muted hover:bg-background-muted transition-colors',
                'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary-base'
              )}
            >
              <Icon name='chevron-down' rotate={90} className='w-3! h-3!' aria-hidden='true' />
            </button>
          )}
          <span className='font-semibold text-foreground-base'>{title}</span>
        </div>

        <div className='py-xs'>{children}</div>

        {hint && <p className='px-xs text-xs text-foreground-muted'>{hint}</p>}

        <div className='flex items-center gap-sm px-xs pt-sm border-t border-border-base'>
          {onClear && (
            <Button variant='ghost' size='sm' onClick={onClear} testId={`${testId}-clear`}>
              {clearLabel ?? t('clear')}
            </Button>
          )}
          <Button
            variant='primary'
            size='sm'
            className='ml-auto'
            onClick={onApply}
            disabled={applyDisabled}
            testId={`${testId}-apply`}
          >
            {applyLabel}
          </Button>
        </div>
      </div>
    );
  }
);

FilterEditorPanel.displayName = 'FilterEditorPanel';
